// The compare screen's arithmetic-free half — P5-3 T9.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THE SAME RULE `estimate.js` OPENS WITH, AND ONE STATED EXCEPTION
// ═══════════════════════════════════════════════════════════════════════════
//
// estimate.js: "Nothing in this file adds, divides, or compares an amount ...
// a second implementation of it in a renderer is how the board and the service
// start disagreeing about what a trip costs."
//
// Every per-person figure on the compare screen is `trips/utils/cost.py`'s,
// arriving in `estimate.by_destination[].overall` — one roll-up per
// destination, computed by the service over that destination's own entries.
// Nothing here recomputes one.
//
// The exception is `delta`, and it is the HANDOFF's: *"`+ £X each` delta in
// amber on the dearer column"*. That is a SUBTRACTION OF TWO FIGURES BOTH
// ALREADY ON THE SCREEN, not a re-derivation of either — it cannot disagree
// with the service about what a trip costs, because it has no opinion about
// what a trip costs. It is fenced three ways:
//
//   1. **Integer minor units, never floats.** Amounts arrive as decimal
//      STRINGS ("306.67") and are parsed to whole cents. `306.67 - 100` in
//      float is 206.67000000000002, and a board that renders that in front of
//      six people has lost the argument the whole product is making.
//   2. **One currency, or one CONVERSION, or no delta at all.** Until T10 this
//      read *"every column must carry exactly one currency and it must be the
//      same one … comparing ¥ to £ needs a rate this product does not have"*.
//      It has one now, so the rule grew a second branch rather than losing its
//      first: a delta is computed either from two figures in the same native
//      currency, or from two CONVERTED figures in the same target currency
//      **from the same as-of date** — and in the second case it says the date.
//      Anything else still refuses, in words, rather than the screen going
//      quiet. See `deltas` for why the condition is what it is.
//   3. **It is never summed with anything.** There is no total on this screen,
//      in any spelling.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 PEOPLE ARE NAMED HERE NOW, AND ONLY HERE — P6-80 FILLED THE SEAM
// ═══════════════════════════════════════════════════════════════════════════
//
// HANDOFF asks for *"must-dos, with the names of who starred them"* — "all 6 of
// you", "only Chri". Until P6-80 the site had no name source at all: ADR-0003 D2
// says there is NO USER TABLE and none is implied, and
// `functions/_lib/discord.js` discards the username on the way through sign-in.
//
// ✅ **P6-63 was decided on 2026-08-21 and the mechanism landed with P6-75a /
// P6-80**: the bot publishes a 3a member directory (nickname → username,
// resolved once, at publish time), the Function at `/api/members` serves it to a
// signed-in member, and `src/directory.js` joins it against ids on the client.
// This function is where that join reaches the screen, and it is still the ONLY
// place on this screen where a user id becomes something a person reads.
//
// ⚠️ NOTHING ELSE ABOUT THIS SCREEN CHANGED. The avatar colours are the same
// colours from the same function, the count sentences are the same sentences,
// and the asymmetry the screen exists to show — six of you starred this, one of
// you starred that — is drawn exactly as it was. What arrived is the identity of
// the one, which is the half HANDOFF asked for and T9 could not deliver.
//
// 🔴 AND IT WORKS WITH NO DIRECTORY AT ALL. Spec §7 item 5: *"kill R2: pages
// render with placeholder names and everything else intact."* Every branch below
// takes its name from `nameFor`, which answers for a directory that is loading,
// unavailable or simply missing an id — never blank, never the raw id. Passing
// no directory is a supported call and produces the screen T9 shipped.

// 🔴 `avatarToken` is imported and NOT re-exported. It is the board's function
// and the dates screen's, and `describePeople` below is the only thing on the
// compare screen allowed to call it — a re-export here would be a second, more
// convenient door onto the decision that function exists to hold in one place.
// The same argument now covers `nameFor`, one field over.
import { avatarToken } from './EntryCard.jsx'
import { honestRollUp, money } from './estimate.js'
import { conversionForDestination } from './enrichment.js'
import { LEFT_SUFFIX, nameFor } from '../directory.js'

/** Column accents, by position. HANDOFF gives Portugal ember, Japan dusk,
 * Morocco verdant; these are the semantic aliases for those three, so a column
 * follows the system rather than a hex from a mock. */
export const PLACE_TOKENS = ['--d2d-accent', '--d2d-accent-alt', '--d2d-positive']

/** The mock's column markers. Three columns is the drawn maximum and the cap
 * the "＋ Another place" slot warns about, but the list is long enough that a
 * fourth place renders a numeral rather than `undefined`. */
export const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X']

/**
 * The three sets the whole screen is derived from.
 *
 * 🔴 `archived` is a set of its own and is NEVER dropped. "Losing destinations
 * are archived, never deleted. Every entry, star, note and unfurled photo
 * survives." A filter that quietly excluded them would make the archive rows —
 * and both flows off them — unreachable, which is the failure the service
 * refuses at the schema level by having no DELETE at all.
 */
export function splitPlaces(destinations) {
  const all = Array.isArray(destinations) ? destinations : []
  const live = all.filter((place) => !place.archived)
  const archived = all.filter((place) => place.archived)
  return { all, live, archived, chosen: live.find((place) => place.chosen) ?? null }
}

/**
 * Does this trip have a comparison at all?
 *
 * 🔴 HANDOFF'S HARDEST RULE, EXPRESSED ONCE, HERE. "With zero or one
 * destination the concept is completely invisible ... The compare screen exists
 * only at ≥2 destinations."
 *
 * ⚠️ It counts ARCHIVED ones too, and that is not a loophole. A trip that
 * chose Japan has one live destination and one archived one, and the archive
 * rows — "Open it, read-only", "Start next year's trip from this" — are this
 * screen's, so it must still exist. A trip that has never had two has neither,
 * and the route sends you back to the board without rendering anything.
 */
export function comparable(destinations) {
  return splitPlaces(destinations).all.length >= 2
}

// ── money, in whole minor units ────────────────────────────────────────────

/** A decimal string from the service, as an integer number of minor units.
 *
 * Returns null for anything that is not one, rather than NaN: a null is a
 * missing delta and a NaN is "+ £NaN each" on a screen about honest numbers.
 *
 * ⚠️ `cost_amount` is `NUMERIC(14, 2)` and cost.py quantises every currency to
 * two places — including the zero-decimal ones, deliberately ("the renderer
 * formats; the store counts"). So two places is the store's precision here and
 * not a guess about currencies.
 */
export function minorUnits(amount) {
  if (typeof amount === 'number') return Number.isFinite(amount) ? Math.round(amount * 100) : null
  if (typeof amount !== 'string') return null
  const match = /^\s*(-?)(\d{1,14})(?:\.(\d{1,2}))?\s*$/.exec(amount)
  if (!match) return null
  const minor = (match[3] ?? '').padEnd(2, '0')
  const value = Number(match[2]) * 100 + Number(minor)
  return match[1] === '-' ? -value : value
}

/** Back to the decimal string `money()` takes. */
export function fromMinorUnits(units) {
  if (!Number.isInteger(units)) return null
  const sign = units < 0 ? '-' : ''
  const absolute = Math.abs(units)
  return `${sign}${Math.trunc(absolute / 100)}.${String(absolute % 100).padStart(2, '0')}`
}

// ── must-dos ───────────────────────────────────────────────────────────────

/**
 * Every ⭐ on one place's entries, most-starred first.
 *
 * `preferences` is `{level: [userId]}` — the service's shape, chosen because a
 * count "cannot be turned back into people". This is the one screen that needs
 * the people rather than the count, which is exactly why that shape matters.
 */
export function mustDos(entries, destinationId) {
  const list = Array.isArray(entries) ? entries : []
  return list
    .filter((entry) => !entry.removed && (entry.destination_id ?? null) === (destinationId ?? null))
    .map((entry) => ({
      id: entry.id,
      title: entry.title,
      starrers: Array.isArray(entry.preferences?.must) ? entry.preferences.must : [],
    }))
    .filter((must) => must.starrers.length > 0)
    .sort(
      (a, b) => b.starrers.length - a.starrers.length || String(a.title).localeCompare(String(b.title)),
    )
}

/**
 * The amber chip's words — HANDOFF's *"only Chri"*, at last.
 *
 * 🔴 THREE SENTENCES, AND THE UNNAMED ONE IS THE ONE T9 SHIPPED. *"only one of
 * you"* is not a degraded *"only Chri"*: it is the true statement that survives
 * without a name, and it is what a member sees when the directory is down or
 * when the starrer joined since the last reconcile. The chip never goes quiet
 * and it never prints an id.
 *
 * A departed member is named and marked in the same breath — spec §4 wants the
 * history readable AND wants the departure visible, and a chip that said "only
 * Chri" about somebody who left six months ago is readable and wrong.
 */
function loneSentence(person) {
  if (person.isYou) return 'only you'
  if (!person.known) return 'only one of you'
  return person.left ? `only ${person.label}, who has ${LEFT_SUFFIX}` : `only ${person.label}`
}

/**
 * ═════════════════════════════════════════════════════════════════════════
 * 🔴 THE ONE PLACE THIS SCREEN TURNS A USER ID INTO SOMETHING A PERSON READS
 * ═════════════════════════════════════════════════════════════════════════
 *
 * Every render of a person on the compare screen comes through here — the
 * avatar dots, the "all 6 of you" line, and the amber "only one of you" chip.
 * `CompareColumn.jsx` never calls `avatarToken` itself and never inspects a
 * user id; it maps over `people` and reads `token`.
 *
 * That is deliberate and it is the seam. **Jordan decided P6-63 on 2026-08-21:
 * the site MAY use Discord names — server nickname first, username when there
 * is no nickname.** T9 reserved the hole; **P6-80 filled it.**
 *
 * The credential question that kept it out of T9 was answered by ADR-0003
 * Amendment A1, and the answer is that the site holds no new credential at all:
 * the BOT publishes the directory (it is the component that already holds the
 * members), and the site reads a 3a blob through a Pages R2 binding. A1.3
 * records the rejected alternative — a GUILD_MEMBERS-privileged bot token on
 * Cloudflare Pages — so that it stays rejected.
 *
 * ⚠️ `directory` IS OPTIONAL AND ITS ABSENCE IS A SUPPORTED STATE, not a bug to
 * be defended against. Three different things arrive here as "no name":
 *
 *     loading        the answer has not landed. Nothing has failed.
 *     unavailable    R2 is down, or the bot has never published.
 *     absent id      a member who joined since the last reconcile.
 *
 * All three render the neutral placeholder, and the SCREEN says which of the
 * three it is (`directoryNote`) rather than this function saying it six times
 * beside six avatars.
 *
 * 🔴 THE GROUP SENTENCES STILL COUNT RATHER THAN LIST, and that is HANDOFF's own
 * wording rather than a limitation: *"all 6 of you"*, *"only Chri"*. Six names
 * in a row under a must-do is a wall of text in a 200px column, and the thing
 * the sentence is for is the ASYMMETRY. So a name appears where HANDOFF puts
 * one — the lone chip — and on each avatar, where it costs no layout at all.
 *
 * `lone` is that amber chip and it is the point of the screen: one person's
 * enthusiasm must not read the same as everyone's.
 */
export function describePeople(userIds, you, headcount, directory) {
  const ids = Array.isArray(userIds) ? userIds : []
  const count = ids.length
  const people = ids.map((id) => {
    const isYou = you != null && id === you
    const { label: name, left, known } = nameFor(directory, id)
    // 🔴 THE READER IS NAMED BY THE SENTENCE ("you and 3 others"), which is how
    // both HANDOFF and T6's dates screen say it. So their own dot carries their
    // name AND "(you)" — the name is added beside the pronoun rather than
    // replacing it, because a row of six names where one of them is quietly
    // yours is a row you have to read twice. When we do not know their name,
    // "you" is the whole answer and the placeholder never appears for the one
    // person the site can always identify.
    const label = known ? (isYou ? `${name} (you)` : name) : isYou ? 'you' : name
    return {
      id,
      token: avatarToken(id),
      isYou,
      known,
      label,
      // Spec §4. Departed members are rendered — history has to stay readable —
      // and rendered DISTINCTLY, because a name that reads as if they are still
      // here is its own wrong answer.
      left,
      // What a hover says. Computed here rather than in the component for the
      // reason the whole function exists: the moment a renderer decides how a
      // departed member reads, there are two places that decide it.
      title: left ? `${label} — ${LEFT_SUFFIX}` : label,
    }
  })
  const mine = people.some((person) => person.isYou)

  if (count === 0) return { count, people, label: null, lone: false }
  if (count === 1) {
    return { count, people, label: null, lone: true, loneLabel: loneSentence(people[0]) }
  }
  if (Number.isFinite(headcount) && headcount > 1 && count >= headcount) {
    return { count, people, label: `all ${headcount} of you`, lone: false }
  }
  if (mine) {
    const others = count - 1
    return {
      count,
      people,
      label: `you and ${others} ${others === 1 ? 'other' : 'others'}`,
      lone: false,
    }
  }
  return { count, people, label: `${count} of you`, lone: false }
}

/**
 * Is anybody drawn on this comparison at all?
 *
 * 🔴 THE NAMES NOTE IS PRINTED ONLY WHERE NAMES ARE, and this is what decides
 * that. A comparison nobody has starred anything on draws no avatars and no
 * "only Chri" chip, so *"Names as of 22 Aug 2026, 02:53 UTC"* underneath it
 * would be a provenance line for data that is not on the screen — the same
 * mistake as an amber "distance unavailable" under a card with no address
 * (`enrichment.js`'s `none` vs `missing`).
 *
 * Archived columns count: they draw must-dos too, and the archive is precisely
 * where a departed member's name matters most (spec §4).
 */
export function namesOnScreen(view) {
  const all = [...(view?.columns ?? []), ...(view?.archived ?? [])]
  return all.some((column) => column.musts.some((must) => must.people.length > 0))
}

/** The mock's `Must-dos · 11 stars on 5, across 6 of 6` header line. */
export function engagement(musts, headcount) {
  const people = new Set()
  let stars = 0
  for (const must of musts) {
    stars += must.starrers.length
    for (const person of must.starrers) people.add(person)
  }
  return { stars, titles: musts.length, people: people.size, headcount }
}

export function mustLine({ stars, titles, people, headcount }) {
  if (titles === 0) return 'nothing starred yet'
  const across = Number.isFinite(headcount) && headcount > 0 ? ` of ${headcount}` : ''
  return `${stars} ${stars === 1 ? 'star' : 'stars'} on ${titles}, across ${people}${across}`
}

/**
 * "only Ra and Mck have starred anything here" — without the names.
 *
 * 🔴 It is a WARNING, not a summary, and the threshold is stated rather than
 * tuned: fewer than half the crew have touched this column, so the numbers
 * above it rest on almost nobody's opinion. The mock renders it BESIDE the
 * must-do list rather than instead of it (HANDOFF's prose says "replaces"; the
 * prototype shows both, and showing both is strictly more information).
 */
export function thinLabel({ people, headcount }) {
  if (!Number.isFinite(headcount) || headcount < 1) return null
  if (people === 0) return 'nobody has starred anything here yet'
  if (people * 2 < headcount) {
    return `only ${people} of ${headcount} of you have starred anything here — treat this as a sketch`
  }
  return null
}

// ── one column ─────────────────────────────────────────────────────────────

/** The four fixed rows' counts and bar widths for one column.
 *
 * Counts come from the service's `by_category` roll-up rather than from
 * counting the cards, for the same reason CategoryRow does: one definition of
 * "on the board" (`removed = FALSE`, in SQL), and a coverage line that cannot
 * disagree with the count printed beside it.
 */
export function categoryBars(byCategory, categories) {
  const rows = categories.map((category) => ({
    key: category.key,
    label: category.label,
    count: Number(byCategory?.[category.key]?.entries_total ?? 0),
  }))
  const max = rows.reduce((highest, row) => Math.max(highest, row.count), 0)
  return rows.map((row) => ({
    ...row,
    width: max === 0 ? '0%' : `${Math.round((row.count / max) * 100)}%`,
  }))
}

/**
 * Every column the comparison renders, with its honesty data.
 *
 * `estimate.by_destination` carries one entry per destination AND — when a trip
 * has destinations and some entries belong to none of them — one with
 * `destination_id: null`. That last one is NOT a column: HANDOFF forbids an
 * "ungrouped" bucket, and a nameless column beside two named ones is exactly
 * one. It comes back separately as `loose`, which the screen states as a
 * sentence rather than drawing as a place.
 */
/** One photo for a destination column, or `null`. **P5-3 T8.**
 *
 * The mocks' column headers are diagonal-stripe blocks, and T9's own comment
 * says *"T8 puts photos here."* This is that — and the interesting part is
 * WHICH photo, because a column has many entries and only one header.
 *
 * The order is the order somebody would point at on the call: a **chosen**
 * entry first (the group already decided it represents this place), then a
 * **stay** (Japan-vs-Portugal is a lodging comparison before it is anything
 * else), then whatever there is. Deterministic at every step, so the same
 * board always draws the same header rather than shuffling on re-render.
 *
 * `null` is ordinary: no entry unfurled, nothing published a photo, or the
 * links were refused. The stripe is the header then, exactly as before.
 */
export function placePhoto(entries, destinationId) {
  const candidates = (Array.isArray(entries) ? entries : []).filter(
    (entry) =>
      !entry?.removed &&
      (entry?.destination_id ?? null) === (destinationId ?? null) &&
      typeof entry?.unfurled?.image_url === 'string' &&
      /^https?:\/\//i.test(entry.unfurled.image_url),
  )
  const chosen = candidates.find((entry) => entry.chosen)
  const stay = candidates.find((entry) => entry.category === 'stay')
  return (chosen ?? stay ?? candidates[0])?.unfurled?.image_url ?? null
}

export function columns(payload, categories, conversion, directory) {
  const { entries = [], estimate, trip, you } = payload ?? {}
  const headcount = Number(trip?.headcount ?? estimate?.headcount ?? 0)
  const perDestination = Array.isArray(estimate?.by_destination) ? estimate.by_destination : []
  const byId = new Map(perDestination.map((row) => [row.destination_id, row]))
  const { live, archived, chosen } = splitPlaces(payload?.destinations)

  const build = (place, index) => {
    const roll = byId.get(place.id)
    // P5-3 T10. The column's own converted figure, from the trip's ONE rate
    // set — never one this file derived. `honestRollUp` puts it on `conversion`
    // and writes the currency sentence.
    const honest = honestRollUp(
      roll?.overall,
      headcount,
      conversionForDestination(conversion, place.id),
    )
    const musts = mustDos(entries, place.id)
    const stats = engagement(musts, headcount)
    return {
      id: place.id,
      name: place.name,
      notes: place.notes ?? null,
      chosen: Boolean(place.chosen),
      archived: Boolean(place.archived),
      roman: ROMAN[index] ?? String(index + 1),
      // T8. `null` when nothing on this place has a photo — the stripe stays.
      photo: placePhoto(entries, place.id),
      token: PLACE_TOKENS[index % PLACE_TOKENS.length],
      entriesTotal: Number(roll?.overall?.entries_total ?? 0),
      // The NUMBER, beside `honest.headcount`, which is the sentence ("at 6
      // people"). The sums panel needs to say "split 6 ways" and a label cannot
      // be put in that hole.
      crew: headcount,
      unpricedSentence: unpricedSentence(roll?.overall),
      ...honest,
      bars: categoryBars(roll?.by_category, categories),
      // 🔴 `starrers` — the raw list of ids — deliberately does NOT survive
      // onto the view model. What a component gets is what `describePeople`
      // decided, which is what stops a second person-rendering rule appearing
      // somewhere downstream.
      musts: musts.map((must) => ({
        id: must.id,
        title: must.title,
        ...describePeople(must.starrers, you, headcount, directory),
      })),
      stats,
      mustLine: mustLine(stats),
      thin: thinLabel(stats),
    }
  }

  const looseRow = byId.get(null) ?? byId.get(undefined) ?? null
  return {
    headcount,
    columns: live.map(build),
    archived: archived.map((place, index) => build(place, live.length + index)),
    chosen: chosen ? build(chosen, live.indexOf(chosen)) : null,
    loose: {
      total: Number(looseRow?.overall?.entries_total ?? 0),
      entries: (Array.isArray(entries) ? entries : []).filter(
        (entry) => !entry.removed && (entry.destination_id ?? null) === null,
      ),
    },
  }
}

// ── the delta ──────────────────────────────────────────────────────────────

/**
 * `+ £448 each` on the dearer columns, or a plain reason there is no delta.
 *
 * Returns `{ deltas: Map<id, string>, reason: string|null }`. The reason is
 * rendered when it is non-null — an absent comparison that says nothing reads
 * as "these are the same", which is the class of silence this product is
 * against.
 */
export function deltas(cols) {
  const list = Array.isArray(cols) ? cols : []
  const deltas = new Map()
  if (list.length < 2) return { deltas, reason: null }

  const unpriced = list.filter((column) => column.figures.length === 0)
  if (unpriced.length > 0) {
    return {
      deltas,
      reason:
        unpriced.length === list.length
          ? 'Nobody has put an amount on any of these yet, so there is nothing to compare.'
          : `${unpriced.length} of these carry no amounts at all, so there is nothing to compare them on yet.`,
    }
  }

  const currencies = new Set()
  for (const column of list) for (const figure of column.figures) currencies.add(figure.currency)
  const singleCurrency = currencies.size === 1 && list.every((column) => column.figures.length === 1)

  if (singleCurrency) {
    const currency = [...currencies][0]
    const units = list.map((column) => minorUnits(column.figures[0].raw))
    if (units.some((value) => value === null)) return { deltas, reason: null }
    fill(deltas, list, units, currency, null)
    return { deltas, reason: null }
  }

  // ═══════════════════════════════════════════════════════════════════════
  // 🔴 P5-3 T10 — THE ANSWER TO "DOES CONVERSION CHANGE THE DELTA'S REFUSAL?"
  // ═══════════════════════════════════════════════════════════════════════
  //
  // It does, and only under a condition worth stating, because the tempting
  // version is wrong in a way nobody would notice.
  //
  // This module's own fence, written by T9: the delta is *"A SUBTRACTION OF
  // TWO FIGURES BOTH ALREADY ON THE SCREEN, not a re-derivation of either — it
  // cannot disagree with the service about what a trip costs, because it has
  // no opinion about what a trip costs."*
  //
  // Once every column renders `≈ £X all in · 21 Aug 2026 rates`, those two
  // numbers ARE both on the screen, in the same unit, from the same rate set,
  // computed by `trips/utils/convert.py`. Subtracting them is the same
  // operation the single-currency branch above already does — and REFUSING
  // would mean declining to subtract two identical-unit figures rendered side
  // by side, which is not a caution, it is an inconsistency. The reader does
  // the subtraction by eye instead, which was the argument for having the
  // delta at all.
  //
  // 🔴 THE CONDITION IS THE PART THAT MATTERS: every column must carry a
  // converted figure, in the SAME target currency, from the SAME `as_of` date.
  // Two columns converted on two different days are two different questions,
  // and their difference belongs to neither. That cannot happen through the
  // current service — one request, one rate set — and it is checked anyway,
  // because "cannot happen upstream" is how a guard stops being real.
  //
  // ⚠️ AND THE DELTA CARRIES THE DATE. TR7 is *"conversions labeled with their
  // as-of date"*, and a delta derived from converted figures is a conversion.
  // A bare `+ £448 each` next to two dated figures would be the one number on
  // the screen whose provenance is not stated.
  const converted = list.map((column) => column.conversion)
  const usable =
    converted.every((item) => item?.state === 'ok' && item.figure && item.raw) &&
    new Set(converted.map((item) => item.currency)).size === 1 &&
    new Set(converted.map((item) => item.asOf)).size === 1

  if (usable) {
    const units = converted.map((item) => minorUnits(item.raw))
    if (units.some((value) => value === null)) return { deltas, reason: null }
    fill(deltas, list, units, converted[0].currency, converted[0].asOf)
    return { deltas, reason: null }
  }

  // Refused — and the sentence is now TRUE, which the old one stopped being.
  // It said *"nothing is converted yet"*; there is a conversion, and what
  // there is not is one for every column. `column.conversion.label` already
  // names the obstacle (a currency with no rate, a feed that is down), so the
  // most specific of those is what gets said.
  const why =
    converted.find((item) => item?.label)?.label ??
    'they are not all converted to one currency'
  return {
    deltas,
    reason: `These are priced in ${[...currencies].sort().join(' and ')} and ${why}, so they cannot be put side by side as one number.`,
  }
}

/** The amber badge on every column dearer than the cheapest one. */
function fill(deltas, list, units, currency, asOf) {
  const cheapest = Math.min(...units)
  list.forEach((column, index) => {
    const gap = units[index] - cheapest
    if (gap > 0) {
      const dated = asOf ? ` · at ${asOf} rates` : ''
      deltas.set(column.id, `+ ${money(fromMinorUnits(gap), currency)} each${dated}`)
    }
  })
}

// ── the archive rows ───────────────────────────────────────────────────────

/**
 * The archive row's sentence.
 *
 * HANDOFF's is *"Archived 18 Aug when Japan was chosen · 7 entries · 5 starred
 * · every note, photo and star Wy collected is still there"*.
 *
 * 🔴 TWO OF ITS FACTS ARE NOT IN THE DATA AND ARE OMITTED RATHER THAN GUESSED.
 * `destination` carries `id, trip_id, name, notes, chosen, archived,
 * created_at` and no `archived_at`, so there is no date to print; and there is
 * no name source for "Wy" (see the header). Both are recorded in the PR body as
 * found-and-not-delivered. What the sentence keeps is the part that carries the
 * promise — nothing was thrown away — because that is the claim the reader has
 * to be able to trust before either button means anything.
 */
/**
 * What the "show the sums" panel closes with — the mock's plain-language note
 * "naming what carries no amount".
 *
 * 🔴 Counting entries, never money. `entries_total` and `entries_priced` are
 * the service's own coverage numbers, the same two the always-visible coverage
 * line is written from, so the panel and the line cannot disagree.
 */
export function unpricedSentence(rollUp) {
  const total = Number(rollUp?.entries_total ?? 0)
  const priced = Number(rollUp?.entries_priced ?? 0)
  if (total === 0) return 'Nothing is on this one yet.'
  const missing = total - priced
  if (missing <= 0) return 'Every entry here carries an amount.'
  return `${missing} of ${total} ${total === 1 ? 'entry' : 'entries'} ${
    missing === 1 ? 'carries' : 'carry'
  } no amount, so this number will move.`
}

export function archiveLine(column, winnerName) {
  const entries = `${column.entriesTotal} ${column.entriesTotal === 1 ? 'entry' : 'entries'}`
  const starred = `${column.stats.titles} starred`
  const when = winnerName ? `Archived when ${winnerName} was chosen` : 'Archived'
  return `${when} · ${entries} · ${starred} · every note and star collected on it is still there`
}
